import * as React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {
  Typography,
  Stack,
  Skeleton,
  Fab,
  Fade,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Virtuoso } from "react-virtuoso";
import { useQuery } from "@tanstack/react-query";

// Components
import Header from "../components/common/header/Header";
import Footer from "../components/common/Footer/Footer";
import ProductCategories from "../components/products/ProductCategories";
import ProductBrands from "../components/products/ProductBrands";
import ProductItem from "../components/products/ProductItem";
import ProductCriteria from "../components/products/ProductCriteria";
import CompareProducts from "../components/products/CompareProducts";
import LocalProductItem from "../components/products/LocalProductItem";
import CustomBreadcrumbs from "../components/common/Breadcrumbs/Breadcrumbs";
import VinalfaOemDiagramSection from "../components/products/VinalfaOemDiagramSection";

// Services & Interfaces
import {
  fetchArticles,
  fetchGenericArticleFacets,
} from "../services/tecdocService";
import { Article } from "../interfaces/Article";
import { CriteriaFilters } from "../interfaces/CriteriaFilters";
import theme from "../theme";

const ProductSearchPage = () => {
  const [categoryId, setCategoryId] = React.useState<number | null>(null);
  const [brandId, setBrandId] = React.useState<number | null>(null);
  const [linkageTargetId, setLinkageTargetId] = React.useState<number | null>(
    null
  );
  const [linkageTargetType, setLinkageTargetType] = React.useState<
    string | null
  >(null);
  const [assemblyGroupNodeIds, setAssemblyGroupNodeIds] = React.useState<
    number | null
  >(null);
  const [criteriaFilters, setCriteriaFilters] = React.useState<
    CriteriaFilters[] | null
  >(null);
  const [comparisonArticles, setComparisonArticles] = React.useState<
    Article[]
  >([]);
  const [page, setPage] = React.useState(1);
  const [keyword, setKeyword] = React.useState("");
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [sectionHash, setSectionHash] = React.useState<string | null>(null);
  const [oemTitle, setOemTitle] = React.useState<string | null>(null);
  const [oemParent, setOemParent] = React.useState<string | null>(null);
  const [vehicleHash, setVehicleHash] = React.useState<string | null>(null);
  /** When browsing OEM diagram, hide TecDoc filters/list until a part number is chosen */
  const [oemPartSelected, setOemPartSelected] = React.useState(false);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const categoryIdParam = urlParams.get("category");
    const brandIdParam = urlParams.get("brand");
    const keywordParam = urlParams.get("keyword");
    const linkageTargetTypeParam = urlParams.get("linkageTargetType");
    const linkageTargetIdParam = urlParams.get("linkageTargetId");
    const assemblyGroupNodeIdsParam = urlParams.get("assemblyGroupNodeIds");
    const sectionHashParam = urlParams.get("sectionHash");
    const oemTitleParam = urlParams.get("oemTitle");
    const oemParentParam = urlParams.get("oemParent");
    const vehicleHashParam = urlParams.get("vehicleHash");

    if (categoryIdParam) setCategoryId(parseInt(categoryIdParam, 10));
    if (brandIdParam) setBrandId(parseInt(brandIdParam, 10));
    if (keywordParam) {
      setKeyword(keywordParam);
      if (sectionHashParam) setOemPartSelected(true);
    }
    if (linkageTargetTypeParam) setLinkageTargetType(linkageTargetTypeParam);
    if (linkageTargetIdParam)
      setLinkageTargetId(parseInt(linkageTargetIdParam, 10));
    if (assemblyGroupNodeIdsParam)
      setAssemblyGroupNodeIds(parseInt(assemblyGroupNodeIdsParam, 10));
    if (sectionHashParam) setSectionHash(sectionHashParam);
    if (oemTitleParam) setOemTitle(oemTitleParam);
    if (oemParentParam) setOemParent(oemParentParam);
    if (vehicleHashParam) setVehicleHash(vehicleHashParam);

    const criteriaParams: CriteriaFilters[] = [];
    urlParams.forEach((value, key) => {
      if (key === "criteria") {
        const [criteriaId, rawValue] = value.split(":");
        criteriaParams.push({
          criteriaId: parseInt(criteriaId, 10),
          rawValue,
        });
      }
    });
    setCriteriaFilters(criteriaParams);
  }, []);

  const handleOemClick = React.useCallback((oem: string) => {
    setOemPartSelected(true);
    setKeyword(oem);
    setPage(1);
    setCategoryId(null);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("keyword", oem);
    urlParams.delete("category");
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${urlParams.toString()}`
    );
  }, []);

  const handleOemSectionChange = React.useCallback(
    (nextHash: string, nextTitle: string) => {
      setSectionHash(nextHash);
      setOemTitle(nextTitle);
      setOemPartSelected(false);
      setKeyword("");
      setPage(1);
      setCategoryId(null);
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("sectionHash", nextHash);
      urlParams.set("oemTitle", nextTitle);
      urlParams.delete("keyword");
      urlParams.delete("category");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams.toString()}`
      );
    },
    []
  );

  const isOemBrowseOnly = !!sectionHash && !oemPartSelected;

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition =
        window.scrollY || document.documentElement.scrollTop;
      setShowScrollTop(scrollPosition > 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const { data: genericArticleFacets } = useQuery({
    queryKey: [
      "genericArticleFacets",
      keyword,
      brandId,
      linkageTargetType,
      linkageTargetId,
      assemblyGroupNodeIds,
    ],
    queryFn: () =>
      fetchGenericArticleFacets(keyword, {
        brand: brandId,
        linkageTargetType,
        linkageTargetId,
        assemblyGroupNodeIds,
      }),
    enabled:
      !isOemBrowseOnly && !!(keyword || brandId || assemblyGroupNodeIds),
  });

  const { data: articlesData, isLoading } = useQuery({
    queryKey: [
      "articles",
      keyword,
      categoryId,
      brandId,
      linkageTargetType,
      linkageTargetId,
      assemblyGroupNodeIds,
      criteriaFilters,
      page,
    ],
    queryFn: () =>
      fetchArticles(keyword, {
        category: categoryId,
        brand: brandId,
        linkageTargetType,
        linkageTargetId,
        assemblyGroupNodeIds,
        criteria: criteriaFilters,
        page,
      }),
    enabled:
      !isOemBrowseOnly &&
      !!(keyword || categoryId || brandId || assemblyGroupNodeIds),
  });

  const updateCategories = React.useCallback((newCategoryId: number) => {
    setCategoryId(newCategoryId);
    setPage(1);
  }, []);

  const updateCriteriaFilters = React.useCallback(
    (criteriaId: number, rawValue: string, isChecked: boolean) => {
      setCriteriaFilters((prevFilters) => {
        const updatedFilters = prevFilters ? [...prevFilters] : [];
        if (isChecked) {
          updatedFilters.push({ criteriaId, rawValue });
        } else {
          return updatedFilters.filter(
            (filter) =>
              filter.criteriaId !== criteriaId || filter.rawValue !== rawValue
          );
        }
        return updatedFilters;
      });
      setPage(1);
    },
    []
  );

  const updateComparisonArticles = React.useCallback(
    (article: Article, isChecked: boolean) => {
      setComparisonArticles((prevArticles) =>
        isChecked
          ? [...prevArticles, article]
          : prevArticles.filter((a) => a !== article)
      );
    },
    []
  );

  // Only visible rows mount → only those fetch live_article_state
  const renderProductItem = React.useCallback(
    (index: number) => {
      if (!articlesData?.articles || index >= articlesData.articles.length) {
        return null;
      }
      const article = articlesData.articles[index];
      return article.tec_doc_article ? (
        <ProductItem
          key={`${article.articleNumber}-${index}`}
          article={article}
          onToggle={updateComparisonArticles}
        />
      ) : (
        <LocalProductItem
          key={`${article.articleNumber}-${index}`}
          article={article}
        />
      );
    },
    [articlesData?.articles, updateComparisonArticles]
  );

  const renderSkeleton = React.useMemo(
    () => (
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Box
          sx={{
            flexGrow: 1,
            m: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={12}>
              <Box>
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={7}
                    sx={{
                      paddingLeft: "0px !important",
                      paddingTop: "0px !important",
                    }}
                  >
                    <CustomBreadcrumbs />
                  </Grid>
                  <Grid item xs={12} sm={12} md={5}>
                    <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
                      <Box sx={{ flexGrow: 1, marginRight: 2 }}>
                        <Skeleton
                          animation="wave"
                          sx={{ width: "200px", height: "30px" }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 3, marginRight: 2 }}>
                        <Skeleton
                          animation="wave"
                          sx={{ width: "200px", height: "30px" }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton
                          animation="wave"
                          sx={{ width: "200px", height: "30px" }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: 4 }}>
                <ProductItem />
              </Box>
            </Grid>
          </Grid>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <Skeleton animation="wave" sx={{ width: "100px" }} />
          </Stack>
        </Box>
      </Stack>
    ),
    []
  );

  return (
    <>
      <Header />
      <Box
        sx={{
          flexGrow: 1,
          m: 2,
          minHeight: "71vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Grid container spacing={3}>
          {sectionHash && (
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: "100%", maxWidth: (t) => t.breakpoints.values.lg }}>
                <VinalfaOemDiagramSection
                  sectionHash={sectionHash}
                  oemTitle={oemTitle}
                  oemParent={oemParent}
                  vehicleHash={vehicleHash}
                  linkageTargetId={
                    linkageTargetId != null ? String(linkageTargetId) : null
                  }
                  linkageTargetType={linkageTargetType}
                  onOemClick={handleOemClick}
                  onSectionChange={handleOemSectionChange}
                />
              </Box>
            </Grid>
          )}

          {sectionHash && oemPartSelected && isLoading && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1.5,
                  py: 3,
                  border: "1px solid #eee",
                  borderRadius: 1,
                  bgcolor: "#fafafa",
                }}
              >
                <CircularProgress size={28} />
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Inter", color: theme.palette.text.primary }}
                >
                  Loading TecDoc articles for {keyword}…
                </Typography>
              </Box>
            </Grid>
          )}

          {!isOemBrowseOnly && (
            <>
              <Grid item xs={12} sm={12} md={2.5}>
                <ProductCategories
                  genericArticles={genericArticleFacets || []}
                  onCategoryChange={updateCategories}
                />
                {categoryId !== null && articlesData?.groupedCriteria && (
                  <ProductCriteria
                    groupedCriteria={articlesData.groupedCriteria}
                    onCriteriaChange={updateCriteriaFilters}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={9.5}>
                <Box
                  sx={{
                    bgcolor: "#f1f1f1",
                    py: 1,
                    px: 2,
                    borderTop: `2px solid ${theme.palette.primary.dark}`,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={5}
                      sx={{ paddingLeft: "0px !important" }}
                    >
                      {articlesData?.assemblyGroupFacets && (
                        <CustomBreadcrumbs
                          assemblyGroupFacets={
                            articlesData.assemblyGroupFacets.counts
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: {
                            xs: "flex-start",
                            sm: "flex-end",
                          },
                          pr: 1,
                        }}
                      >
                        <Box
                          sx={{
                            mr: 1,
                            display: {
                              xs: "none",
                              sm: "block",
                              md: "block",
                              lg: "block",
                            },
                          }}
                        >
                          <CompareProducts compareArticles={comparisonArticles} />
                        </Box>
                        {articlesData?.dataSupplierFacets && (
                          <Box sx={{ mr: 1 }}>
                            <ProductBrands
                              dataSupplierFacets={articlesData.dataSupplierFacets}
                            />
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ mt: 4 }}>
                  {isLoading ? (
                    renderSkeleton
                  ) : !articlesData?.articles ||
                    articlesData.articles.length === 0 ? (
                    <Typography variant="h5" textAlign="center" mt={10}>
                      No products found!
                    </Typography>
                  ) : (
                    <Virtuoso
                      useWindowScroll
                      style={{ height: "calc(100vh - 150px)", width: "100%" }}
                      totalCount={articlesData.articles.length}
                      itemContent={renderProductItem}
                      overscan={600}
                      increaseViewportBy={{ top: 600, bottom: 600 }}
                      defaultItemHeight={200}
                    />
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      <Footer />
      <Fade in={showScrollTop}>
        <Fab
          color="secondary"
          aria-label="scroll to top"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Fade>
    </>
  );
};

export default ProductSearchPage;
